// Tetris game engine - pure logic, no React dependency

export const BOARD_WIDTH = 10;
export const BOARD_HEIGHT = 20;

export type CellType = '' | 'I' | 'O' | 'T' | 'S' | 'Z' | 'J' | 'L' | 'ghost';
export type Board = CellType[][];

export interface Position { x: number; y: number; }

export interface Piece {
  type: Exclude<CellType, '' | 'ghost'>;
  shape: number[][];
  pos: Position;
}

const SHAPES: Record<string, number[][]> = {
  I: [[0,0,0,0],[1,1,1,1],[0,0,0,0],[0,0,0,0]],
  O: [[1,1],[1,1]],
  T: [[0,1,0],[1,1,1],[0,0,0]],
  S: [[0,1,1],[1,1,0],[0,0,0]],
  Z: [[1,1,0],[0,1,1],[0,0,0]],
  J: [[1,0,0],[1,1,1],[0,0,0]],
  L: [[0,0,1],[1,1,1],[0,0,0]],
};

const PIECE_TYPES: CellType[] = ['I', 'O', 'T', 'S', 'Z', 'J', 'L'];

export function createEmptyBoard(): Board {
  return Array.from({ length: BOARD_HEIGHT }, () => Array(BOARD_WIDTH).fill(''));
}

export function randomPiece(): Piece {
  const type = PIECE_TYPES[Math.floor(Math.random() * PIECE_TYPES.length)] as Piece['type'];
  return {
    type,
    shape: SHAPES[type].map(r => [...r]),
    pos: { x: Math.floor((BOARD_WIDTH - SHAPES[type][0].length) / 2), y: 0 },
  };
}

export function isValidPosition(board: Board, piece: Piece, pos: Position): boolean {
  for (let y = 0; y < piece.shape.length; y++) {
    for (let x = 0; x < piece.shape[y].length; x++) {
      if (!piece.shape[y][x]) continue;
      const nx = pos.x + x;
      const ny = pos.y + y;
      if (nx < 0 || nx >= BOARD_WIDTH || ny >= BOARD_HEIGHT) return false;
      if (ny < 0) continue;
      if (board[ny][nx] !== '') return false;
    }
  }
  return true;
}

export function rotatePiece(piece: Piece): number[][] {
  const n = piece.shape.length;
  const rotated: number[][] = Array.from({ length: n }, () => Array(n).fill(0));
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      rotated[x][n - 1 - y] = piece.shape[y][x];
    }
  }
  return rotated;
}

export function placePiece(board: Board, piece: Piece): Board {
  const newBoard = board.map(r => [...r]);
  for (let y = 0; y < piece.shape.length; y++) {
    for (let x = 0; x < piece.shape[y].length; x++) {
      if (!piece.shape[y][x]) continue;
      const ny = piece.pos.y + y;
      const nx = piece.pos.x + x;
      if (ny >= 0 && ny < BOARD_HEIGHT && nx >= 0 && nx < BOARD_WIDTH) {
        newBoard[ny][nx] = piece.type;
      }
    }
  }
  return newBoard;
}

export function clearLines(board: Board): { newBoard: Board; linesCleared: number } {
  const newBoard = board.filter(row => row.some(cell => cell === ''));
  const linesCleared = BOARD_HEIGHT - newBoard.length;
  while (newBoard.length < BOARD_HEIGHT) {
    newBoard.unshift(Array(BOARD_WIDTH).fill(''));
  }
  return { newBoard, linesCleared };
}

export function getGhostPosition(board: Board, piece: Piece): Position {
  let ghostY = piece.pos.y;
  while (isValidPosition(board, piece, { x: piece.pos.x, y: ghostY + 1 })) {
    ghostY++;
  }
  return { x: piece.pos.x, y: ghostY };
}

export function calculateScore(linesCleared: number, level: number): number {
  const points = [0, 100, 300, 500, 800];
  return (points[linesCleared] || 0) * (level + 1);
}

export function getLevel(totalLines: number): number {
  return Math.floor(totalLines / 10);
}

export function getDropInterval(level: number): number {
  return Math.max(100, 1000 - level * 80);
}
